import { useState, useCallback, useMemo, type KeyboardEvent } from 'react'
import { Loader2, SendHorizontal } from 'lucide-react'
import { Virtuoso } from 'react-virtuoso'
import UserAvatar from '@/assets/user-avatar'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { LottieChat } from '@/components/chat-lottie'
import { useUser } from '@/features/auth'
import { useAuthStore } from '@/features/auth/store/auth-store'
import { useEnginePlayActions } from '../hooks/use-engine-play-action'
import { useShareActions } from '../hooks/use-share-action'
import { useEngineStore } from '../stores/use-engine-store'
import { SenderRole } from '../types/chat-model'
import { ChatActionButton } from './chat-action-button'
import { ChatSettingsTrigger } from './chat-settings'
import { ChatboxTemplateRenderer } from './chatbox-render'
import { CopyMessageButton, GoToMessageButton } from './galgame-tools'
import { ShareMessageButton, ShareSelectionBar } from './share'

interface ChatContainerProps {
  sessionId: string
}

export default function ChatContainer({ sessionId }: ChatContainerProps) {
  const { auth } = useAuthStore()
  const { userProfile } = useUser()
  const avatarUrl = userProfile?.avatarUrl ?? null
  const { user } = auth
  const { messages: messagesSlice, engine, shareMode } = useEngineStore()
  const { currentEngine } = engine
  const { hasMore, isLoadingMore } = messagesSlice
  const { isShareMode, selectedMessageIds } = shareMode
  const { toggleMessage } = useShareActions()

  const { sendText, isPolling, isMessagesLoading, fetchNextPage } =
    useEnginePlayActions(sessionId)

  const messages = messagesSlice.messages

  // 输入框状态
  const [inputValue, setInputValue] = useState('')
  const [isSending, setIsSending] = useState(false)

  // Virtuoso firstItemIndex: 使用大数字递减模式，确保向上加载时列表位置不跳动
  const FIRST_ITEM_INDEX = 1000000
  const firstItemIndex = useMemo(() => {
    return FIRST_ITEM_INDEX - (messages?.length ?? 0)
  }, [messages?.length])

  // 上拉加载更多历史消息
  const handleLoadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return
    fetchNextPage?.()
  }, [hasMore, isLoadingMore, fetchNextPage])

  // 发送消息处理
  const handleSend = useCallback(async () => {
    const text = inputValue.trim()
    if (!text || isSending) return

    setIsSending(true)
    setInputValue('')

    try {
      await sendText(text)
    } finally {
      setIsSending(false)
    }
  }, [inputValue, isSending, sendText])

  // 键盘事件处理：Enter 发送，Shift+Enter 换行
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  // 加载状态：正在获取消息
  if (isMessagesLoading) {
    return (
      <div className='flex h-full w-full items-center justify-center'>
        <LottieChat className='h-24 w-24' loop />
      </div>
    )
  }

  if (!messages || !currentEngine)
    return (
      <div className='text-muted-foreground bg-background flex h-full items-center justify-center'>
        无消息记录
      </div>
    )

  // 判断是否为空消息列表
  const isEmpty = messages.length === 0 && !isMessagesLoading

  return (
    <div className='chat-container relative flex h-full w-full flex-col'>
      {/* 消息滚动区域 */}
      <div className='min-h-0 flex-1 overflow-x-hidden'>
        {isEmpty ? (
          // 空消息提示
          <div className='flex h-full flex-col items-center justify-center px-4'>
            <div className='animate-in fade-in-0 zoom-in-95 text-center duration-500'>
              <div className='text-muted-foreground/30 mb-4 text-6xl'>💬</div>
              <p className='text-muted-foreground mb-2 text-lg font-medium'>
                目前没有开场白
              </p>
              <p className='text-muted-foreground/60 text-sm'>
                手动发一条消息试试？
              </p>
            </div>
          </div>
        ) : (
          <Virtuoso
            style={{ height: '100%' }}
            firstItemIndex={firstItemIndex}
            initialTopMostItemIndex={messages.length - 1}
            data={messages}
            followOutput='smooth'
            startReached={handleLoadMore}
            components={{
              // 顶部加载指示器
              Header: () =>
                isLoadingMore ? (
                  <div className='flex items-center justify-center py-4'>
                    <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
                    <span className='text-muted-foreground ml-2 text-sm'>
                      加载更多...
                    </span>
                  </div>
                ) : hasMore ? (
                  <div className='text-muted-foreground/50 py-4 text-center text-xs'>
                    上拉加载更多历史
                  </div>
                ) : messages.length > 0 ? (
                  <div className='text-muted-foreground/50 py-4 text-center text-xs'>
                    已加载全部消息
                  </div>
                ) : null,
              // 移动端预留更少高度，PC保持
              Footer: () => <div className='h-32 md:h-44' />,
            }}
            itemContent={(index, message) => {
              // 将 Virtuoso 虚拟索引转换为 messages 数组的真实索引
              const realMsgIndex = index - firstItemIndex
              const isUser = message.role === SenderRole.USER
              const isSelected = selectedMessageIds.has(message.id)
              return (
                <div
                  key={message.id}
                  className={cn(
                    'w-full cursor-pointer py-5 transition-colors md:py-8',
                    isShareMode && isSelected && 'bg-primary/10',
                    isShareMode && 'hover:bg-primary/5'
                  )}
                  onClick={() => isShareMode && toggleMessage(message.id)}
                >
                  <div
                    className={cn(
                      'mx-auto flex w-full max-w-3xl px-4 md:max-w-[80%] md:px-6 lg:max-w-4xl',
                      // 移动端：纵向排列，PC端：横向排列
                      'flex-col md:flex-row md:gap-4',
                      isUser
                        ? 'items-end md:flex-row-reverse'
                        : 'items-start md:flex-row'
                    )}
                  >
                    {/* 头像区域 (移动端：与名称横向排列并置于上方) */}
                    <div
                      className={cn(
                        'mb-2 flex items-center gap-2 md:mb-0 md:shrink-0 md:flex-col',
                        isUser ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      <Avatar className='border-border/50 h-8 w-8 border shadow-sm md:h-10 md:w-10'>
                        {isUser ? (
                          avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt='Avatar'
                              className='h-full w-full object-cover'
                            />
                          ) : (
                            <UserAvatar id={user?.id ?? '1'} size={40} />
                          )
                        ) : currentEngine.coverURL ? (
                          <img
                            src={currentEngine.coverURL}
                            alt='Avatar'
                            className='h-full w-full object-cover'
                          />
                        ) : (
                          <UserAvatar id={currentEngine.id} size={40} />
                        )}
                      </Avatar>
                      {/* 发送者名称 (移动端特有) */}
                      <div
                        className={cn(
                          'flex items-center gap-1.5 md:hidden',
                          isUser ? 'flex-row-reverse' : 'flex-row'
                        )}
                      >
                        <span className='text-primary text-[10px] font-bold tracking-wider uppercase opacity-100'>
                          {isUser ? 'You' : currentEngine?.title}
                        </span>
                        <CopyMessageButton message={message} />
                        <GoToMessageButton msgIndex={realMsgIndex} />
                        <ShareMessageButton messageId={message.id} />
                      </div>
                    </div>

                    {/* 消息内容容器 */}
                    <div
                      className={cn(
                        'group flex w-full min-w-0 flex-col',
                        isUser ? 'items-end' : 'items-start'
                      )}
                    >
                      {/* 发送者名称 (PC端) */}
                      <div
                        className={cn(
                          'mb-2 hidden items-center gap-2 px-1 md:flex',
                          isUser ? 'flex-row-reverse' : 'flex-row'
                        )}
                      >
                        <span className='text-primary text-xs font-bold tracking-wider uppercase opacity-100'>
                          {isUser ? 'You' : currentEngine?.title}
                        </span>
                        <div className='flex items-center gap-1 transition-opacity'>
                          <CopyMessageButton message={message} />
                          <GoToMessageButton msgIndex={realMsgIndex} />
                          <ShareMessageButton messageId={message.id} />
                        </div>
                      </div>

                      {/* 消息主体 */}
                      <div
                        className={cn(
                          'text-foreground text-[15px] leading-relaxed tracking-wide md:text-base md:leading-loose',
                          'w-full min-w-0 overflow-hidden px-4 py-2.5 font-sans md:w-auto md:font-serif',
                          isUser
                            ? 'rounded-2xl bg-[#d9f0ff] dark:bg-blue-900/40'
                            : 'bg-background rounded-2xl'
                        )}
                      >
                        <ChatboxTemplateRenderer
                          message={message}
                          isLatest={realMsgIndex === messages.length - 1}
                          context={undefined}
                        />
                        {/* 操作按钮 (仅最新一条 AI 消息，且不是第一条消息，常态显示) */}
                        {realMsgIndex === messages.length - 1 &&
                          realMsgIndex !== 0 &&
                          !isUser && (
                            <div className='flex items-center gap-1.5 pt-1'>
                              <ChatActionButton
                                sessionId={sessionId}
                                className='hover:bg-primary/10 hover:text-primary text-primary/70 hover:text-primary h-7 w-7 rounded-full text-xs'
                              />
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            }}
          />
        )}
      </div>

      {/* 底部输入区域 (居中响应式) */}
      <div className='from-background via-background/90 absolute right-0 bottom-0 left-0 z-20 bg-linear-to-t to-transparent pt-6 md:pt-10'>
        <div className='mx-auto w-full max-w-3xl px-3 pb-4 md:px-4 md:pb-8 lg:max-w-4xl'>
          {/* 轮询等待动画 - 固定在输入框上方 */}
          {isPolling && (
            <div className='animate-in fade-in-0 slide-in-from-bottom-2 mb-3 flex justify-center duration-300'>
              <div className='bg-card/80 border-border/40 flex items-center gap-2 rounded-full border px-4 py-2 shadow-lg backdrop-blur-md'>
                <LottieChat className='h-8 w-8' loop />
                <span className='text-muted-foreground text-sm'>
                  正在思考中...
                </span>
              </div>
            </div>
          )}
          <div className='group border-border/60 bg-card/80 focus-within:border-primary/50 focus-within:ring-primary/10 relative flex flex-col gap-1.5 rounded-2xl border p-2 shadow-lg backdrop-blur-md transition-all focus-within:ring-2 md:gap-2 md:p-3'>
            {/* 顶辅助文字 - 移动端隐藏以节省空间 */}
            <div className='text-muted-foreground/40 hidden px-2 text-[10px] transition-opacity group-focus-within:opacity-100 md:block'>
              按 Enter 发送，Shift + Enter 换行
            </div>

            <Textarea
              placeholder='世界观越复杂、历史记忆越多，元点消耗成本越高...'
              className='max-h-32 min-h-[36px] resize-none border-none bg-transparent p-1.5 text-base shadow-none focus-visible:ring-0 md:max-h-48 md:min-h-[40px] md:p-2 md:text-sm'
              rows={1}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending}
            />

            {/* 底部操作栏 */}
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-0.5 md:gap-1'>
                <ChatSettingsTrigger />

                <div className='bg-border/40 mx-1.5 h-3.5 w-px md:mx-2 md:h-4' />

                {/* 轮询状态提示 */}
                {(isSending || isPolling) && (
                  <span className='text-muted-foreground animate-pulse text-xs'>
                    正在处理...
                  </span>
                )}
              </div>

              <Button
                size='icon'
                className='bg-primary text-primary-foreground h-8 w-8 rounded-full shadow-sm transition-transform hover:scale-105 active:scale-95 disabled:opacity-50'
                onClick={handleSend}
                disabled={!inputValue.trim() || isSending}
              >
                <SendHorizontal className='size-4' />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* 分享选择操作栏 */}
      <ShareSelectionBar />
    </div>
  )
}
